/**
 * @file    MetricsManager.tsx
 * @scope   VIEW
 * @summary CPR rollup metrics list: each row is a rollup (name + program count) with reorder,
 *          remove, inline rename of the title and a "Manage programs" entry point.
 * @consumes useMetricsManager, SortableListItem
 * @produces `MetricsManager` component
 * @sideEffects None directly — state lives in useMetricsManager.
 * @notes Presenter half of the Container/Presenter split; keep it declarative.
 */
import React from 'react';
import SortableListItem from '../../../../common/components/SortableListItem/SortableListItem';
import { useMetricsManager } from './useMetricsManager';
import { getProgramCountLabel, getRollupErrorMessage } from './metricsManagerModel';
import {
  RollupMetricModel,
  getRollupProgramCount,
} from 'models/CrossProgramRollup/RollupMetricModel';
import './MetricsManager.scss';

interface MetricsManagerProps {
  /** Navigates to the programs selection of the given rollup. */
  onManagePrograms?: (rollupId: string) => void;
}

const MetricsManager: React.FC<MetricsManagerProps> = ({ onManagePrograms }) => {
  const {
    items,
    globalState,
    itemStates,
    showEmptyError,
    getItemErrors,
    handleAddRollup,
    handleRemoveRollup,
    handleRenameRollup,
    handleTitleFocus,
    handleTitleBlur,
    handleMetricLeave,
    handleDragStart,
    handleDragOver,
    handleDrop,
  } = useMetricsManager();

  const handleManageClick = (e: React.MouseEvent, id: string, disabled: boolean) => {
    e.preventDefault();
    if (!disabled) onManagePrograms?.(id);
  };

  const renderItem = (item: RollupMetricModel, index: number) => {
    const state = itemStates.get(index);
    if (!state) return null;
    const errors = getItemErrors(item);
    const hasNameError = errors.includes('missingName');
    const hasProgramsError = errors.includes('noPrograms');

    return (
      <SortableListItem
        key={item.id}
        id={item.id}
        index={index}
        dragState={state.Drag}
        deleteState={state.Delete}
        isInvalid={errors.length > 0}
        invalidMessage={getRollupErrorMessage(errors)}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onDelete={handleRemoveRollup}
        onFocusLeave={handleMetricLeave}
      >
        <input
          className={`metrics-manager__title-input${
            hasNameError ? ' metrics-manager__title-input--invalid' : ''
          }`}
          value={item.displayName}
          aria-label="Cross program name"
          aria-invalid={hasNameError}
          onFocus={() => handleTitleFocus(item)}
          onBlur={() => handleTitleBlur(item.id)}
          onChange={(e) => handleRenameRollup(item.id, e.target.value)}
        />
        <div
          className={`dataSourceFieldsLabel${
            hasProgramsError ? ' dataSourceFieldsLabel--invalid' : ''
          }`}
        >
          <span className="dataSourceFieldsCounterLabel">
            {getProgramCountLabel(getRollupProgramCount(item))}
          </span>
        </div>
        {state.ManagePrograms.visible && (
          <div className="manage-fields-container">
            <a
              href="#"
              className={`manage-fields-link${
                state.ManagePrograms.disabled ? ' manage-fields-link--disabled' : ''
              }`}
              onClick={(e) => handleManageClick(e, item.id, state.ManagePrograms.disabled)}
            >
              Manage programs
            </a>
          </div>
        )}
      </SortableListItem>
    );
  };

  return (
    <div className="metrics-manager">
      <p className="metrics-manager__description">
        Each metric below rolls up the same question across multiple programs into one combined
        score.
      </p>

      {items.length > 0 && <div className="dataSourceContainer">{items.map(renderItem)}</div>}

      {showEmptyError && (
        <div className="diy-required-field-wrapper">
          <span className="diy-required-field">You must add at least one rollup metric</span>
        </div>
      )}

      {globalState.AddRollup.visible && (
        <button
          type="button"
          className="metrics-manager__add"
          disabled={globalState.AddRollup.disabled}
          onClick={handleAddRollup}
        >
          Add rollup metric
        </button>
      )}
    </div>
  );
};

export default MetricsManager;
